import { useState, useEffect, ChangeEvent } from "react";
import { Box, FormControlLabel, Checkbox } from "@mui/material";
import SbuxTextField from "../../../../../components/SubxTextField";
import SbuxTranslate from "../../../../../components/SbuxTranslate";
import MainHoursConfigTable from "../mainHours/MainHoursConfigTable";
import useTextTranslate from "../../../hooks/useTextTranslate";
import styles from "./styles";
import useCss from "../../../../../hooks/useCss";

interface RowData {
  hoursCode?: string | number;
  hoursName?: string;
  description?: string;
  showForManagers?: boolean;
  readOnly?: boolean;
  hoursConfig?: Record<string, unknown>[];
  [key: string]: unknown;
}

interface FormValues {
  hoursCode: string | number | undefined;
  hoursName: string;
  description: string;
  showForManagers: boolean;
  readOnly: boolean;
}

interface Props {
  row: RowData;
  memoDataset: (data: Record<string, unknown>) => void;
  isManager?: boolean;
}

const MainHoursEdit = ({ row, memoDataset, isManager }: Props) => {
  const classes = useCss(styles);
  const hoursNamePlaceholder = useTextTranslate("Enter hours name");
  const descriptionPlaceholder = useTextTranslate("Enter description");
  const initFormValues: FormValues = {
    hoursCode: row.hoursCode,
    hoursName: row.hoursName ?? "",
    description: row.description ?? "",
    showForManagers: row.showForManagers || false,
    readOnly: row.readOnly || false,
  };

  const [formValue, setFormValue] = useState<FormValues>(initFormValues);
  const [hoursNameError, sethoursNameError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const isFormDisabled = isManager && formValue.readOnly;
  useEffect(() => {
    memoDataset({ ...row } as Record<string, unknown>);
    setTimeout(() => {
      setIsLoading(false);
    }, 100);
  }, []);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const data = { ...formValue, [name]: value };

    setFormValue(data);
    name === "hoursName" && sethoursNameError(value.length < 1 ? true : false);

    memoDataset({ [name]: value });
  };

  const handleCheckboxChange =
    (property: string) => (e: ChangeEvent<HTMLInputElement>) => {
      const { checked } = e.target;
      const updatedValues = { ...formValue, [property]: checked };

      if (property === "showForManagers" && !checked) {
        updatedValues.readOnly = false;
      }

      setFormValue(updatedValues);
      // Only send the changed fields — NOT the full formValue which would
      // overwrite hoursConfig managed independently by MainHoursConfigTable.
      const allValues = updatedValues as Record<string, unknown>;
      const { hoursConfig: _ignored, ...partialUpdate } = allValues;
      memoDataset(partialUpdate);
    };

  return (
    <Box
      component="form"
      noValidate
      autoComplete="off"
      className={classes.formControl}
    >
      <Box>
        <SbuxTextField
          variant="outlined"
          label="#"
          disabled
          preventAutoFocus
          handleChange={handleChange}
          value={String(formValue.hoursCode ?? "")}
          name="hoursCode"
        />
        <SbuxTextField
          variant="outlined"
          label={<SbuxTranslate>{"Hours Name"}</SbuxTranslate>}
          isError={hoursNameError}
          placeholder={hoursNamePlaceholder}
          handleChange={handleChange}
          value={formValue.hoursName}
          name="hoursName"
          errorMessage={
            <SbuxTranslate>{"At least one character required"}</SbuxTranslate>
          }
          error={hoursNameError}
          required
          disabled={isManager}
        />
        <SbuxTextField
          variant="outlined"
          placeholder={descriptionPlaceholder}
          label={<SbuxTranslate>{"Description"}</SbuxTranslate>}
          handleChange={handleChange}
          value={formValue.description}
          preventAutoFocus
          name="description"
          disabled={isManager}
        />
        <Box>
          <FormControlLabel
            className="disabledLabel"
            control={
              <Checkbox
                className="checkboxStyle"
                inputProps={{ "aria-label": "Manager Access" }}
                disableFocusRipple
                disableRipple
                checked={formValue.showForManagers}
                onChange={handleCheckboxChange("showForManagers")}
                disabled={isManager}
              />
            }
            label={<SbuxTranslate>{"Manager Access"}</SbuxTranslate>}
          />
        </Box>
        {formValue.showForManagers && (
          <Box>
            <FormControlLabel
              className="disabledLabel"
              control={
                <Checkbox
                  className="readOnlyCheckboxStyle"
                  inputProps={{ "aria-label": "Manager Read-only" }}
                  disableFocusRipple
                  disableRipple
                  checked={formValue.readOnly}
                  onChange={handleCheckboxChange("readOnly")}
                  disabled={isManager}
                />
              }
              label={<SbuxTranslate>{"Manager Read-only"}</SbuxTranslate>}
            />
          </Box>
        )}
      </Box>

      <Box className="fullWidth readOnlyTable">
        <MainHoursConfigTable
          rowData={{ ...row }}
          memoDataset={memoDataset}
          isLoading={isLoading}
          isTableDisabled={isFormDisabled ?? false}
        />
      </Box>
    </Box>
  );
};

export default MainHoursEdit;
